// @givmo/board — placing a consent's compensation block in its text (CBG-YR4-PAY-CONSENT; the
// backend's `board-compensation-decisions.md` §7.2, §7.3, §7.5).
//
// A written consent that records pay decisions carries ONE block, rendered by the backend from all
// its decisions, in the text the directors sign: the consent cannot go out, or be revised, unless its
// text carries the CURRENT block exactly. Board serves the block; the officer's act places it ("Add
// to the consent's text"), and nothing else in the officer's words moves.
//
// So the block goes in once, at the end of the text, after a blank line; and when the details changed
// after it was placed, the block the text carries is replaced where it stands. A block is read by the
// lines the backend prints (§7.3), never by indentation alone, because the officer's own text may be
// indented too (the house's consent template ends with four-space signature lines):
//   * the heading, "Compensation details as recorded:", unindented;
//   * a decision's two-space lines: "For:", "Documents the board relied on:", "How the board obtained
//     them:" and "The board’s reason for setting pay outside the range in that information:", and a
//     committee's own three (cut yr6meet): "Documents the committee relied on:", "How the committee
//     obtained them:" and "The committee’s reason …" — a line of either body's block is the block's;
//   * a document's four-space line, ending in its fingerprint;
//   * a later line of an answer, four spaces in, under the answer or another of its lines (a blank
//     line between two paragraphs of an answer stays blank).
//
// ⚠ ONE LINE IS AMBIGUOUS BY CONSTRUCTION: a four-space line directly under an answer reads as the
// answer's own next line, whoever typed it. Across a blank line, a four-space paragraph is the block's
// only when the current block holds it word for word or the block goes on after it; otherwise it is
// left in place as the officer's (so a stale answer's changed last paragraph can stay behind).
//
// The backend reads a block by the same rule (CBG-FIX84, `board-schedule-o.md` §F1): the service's
// `carries_block` and the version's CHECK make exactly the test `carriesBlock` makes, so the panel and
// the circulation agree. One case table holds the three readers to it (the backend's
// `tests/support/board_compensation_block_cases.py`, carried here in `compensation-block.test.ts`).

/** The block's first line, as the backend renders it (§7.3, unindented in a consent's text). */
export const BLOCK_HEADING = 'Compensation details as recorded:';

/** The two-space lines that open an answer, whose later lines run on four spaces in: both bodies'. */
const ANSWER_PREFIXES = [
  '  How the board obtained them: ',
  '  How the committee obtained them: ',
  '  The board’s reason for setting pay outside the range in that information: ',
  '  The committee’s reason for setting pay outside the range in that information: ',
] as const;
/** The two-space lines a decision's section opens its parts with (§7.3), of both bodies. */
const DETAIL_PREFIXES = [
  '  For: ',
  '  Documents the board relied on:',
  '  Documents the committee relied on:',
  ...ANSWER_PREFIXES,
] as const;
/**
 * A document the board relied on: four spaces in, its name, and its fingerprint. Its `.` stops at a
 * carriage return and at U+2028 and U+2029, which a consent's text may hold; the backend and the CHECK
 * spell that out (`[^\n\r\u2028\u2029]`), so one pasted line reads one way on both sides.
 */
const DOCUMENT_LINE = /^ {4}.*\(fingerprint [0-9a-f]{64}\)$/;

/** A line only the block prints: its heading, a decision's part, or a document. */
function isMarker(line: string): boolean {
  return (
    line === BLOCK_HEADING ||
    DETAIL_PREFIXES.some((prefix) => line.startsWith(prefix)) ||
    DOCUMENT_LINE.test(line)
  );
}

function isAnswerLine(line: string): boolean {
  return ANSWER_PREFIXES.some((prefix) => line.startsWith(prefix));
}

function isContinuation(line: string): boolean {
  return line.startsWith('    ') && !DOCUMENT_LINE.test(line);
}

/** Whether `line` reads as a later line of the answer `previous` is part of. */
function continuesAnswer(previous: string, line: string): boolean {
  return isContinuation(line) && (isAnswerLine(previous) || isContinuation(previous));
}

function isBlank(line: string | undefined): boolean {
  return line !== undefined && line.trim().length === 0;
}

/** Where `block`'s lines stand in the text's lines, whole lines in order; -1 where they don't. */
function indexOfLines(lines: readonly string[], block: readonly string[]): number {
  for (let start = 0; start + block.length <= lines.length; start += 1) {
    if (block.every((line, offset) => lines[start + offset] === line)) return start;
  }
  return -1;
}

/**
 * Where an earlier block stands in the text's lines: its first heading, and the line after its last.
 * It runs on through the lines directly under it that the block prints, and across blank lines only to
 * one of its own kind; a blank-separated paragraph of an answer joins it only when `current` holds the
 * same line or the block goes on after it. Every other line is the officer's, and ends it.
 */
function earlierBlock(
  lines: readonly string[],
  current: readonly string[],
): { start: number; end: number } | null {
  const start = lines.indexOf(BLOCK_HEADING);
  if (start === -1) return null;
  let end = start + 1;
  while (end < lines.length) {
    const last = lines[end - 1] ?? '';
    const line = lines[end] ?? '';
    if (isMarker(line) || continuesAnswer(last, line)) {
      end += 1;
      continue;
    }
    if (!isBlank(line)) break;
    let next = end;
    while (isBlank(lines[next])) next += 1;
    const following = lines[next];
    if (following === undefined) break;
    if (isMarker(following)) {
      end = next + 1;
      continue;
    }
    if (!continuesAnswer(last, following)) break;
    let paragraphEnd = next + 1;
    while (
      paragraphEnd < lines.length &&
      continuesAnswer(lines[paragraphEnd - 1] ?? '', lines[paragraphEnd] ?? '')
    ) {
      paragraphEnd += 1;
    }
    let after = paragraphEnd;
    while (isBlank(lines[after])) after += 1;
    const goesOn = after < lines.length && isMarker(lines[after] ?? '');
    if (!current.includes(following) && !goesOn) break;
    end = paragraphEnd;
  }
  return { start, end };
}

/**
 * Whether the text carries `block` EXACTLY: its lines stand in the text whole, the line directly
 * under them is not one the block prints (what an earlier block left behind), and the heading appears
 * nowhere else in the text. So a stale block that begins with the current one — the officer clears the
 * reason on the last decision, or removes the last of two people — is not carried, and the panel
 * offers to put the current block in its place. The backend's `carries_block` and the version's
 * CHECK make the same test (CBG-FIX84), so a text this passes circulates.
 */
export function carriesBlock(body: string, block: string | null): boolean {
  if (block === null || block.length === 0) return false;
  const lines = body.split('\n');
  const blockLines = block.split('\n');
  const at = indexOfLines(lines, blockLines);
  if (at === -1) return false;
  const last = blockLines[blockLines.length - 1] ?? '';
  const under = lines[at + blockLines.length];
  if (under !== undefined && (isMarker(under) || continuesAnswer(last, under))) return false;
  const headings = (text: readonly string[]) =>
    text.filter((line) => line === BLOCK_HEADING).length;
  return headings(lines) === headings(blockLines);
}

/** Whether the text carries a block at all: its heading, on a line of its own. */
export function holdsBlock(body: string): boolean {
  return body.split('\n').includes(BLOCK_HEADING);
}

/**
 * The consent's text with `block` in it: unchanged when it already carries the block exactly; the
 * earlier block replaced where it stands, every other line kept; else the block after the officer's
 * words and a blank line.
 */
export function placeBlock(body: string, block: string): string {
  if (block.length === 0 || carriesBlock(body, block)) return body;
  const lines = body.split('\n');
  const earlier = earlierBlock(lines, block.split('\n'));
  if (earlier !== null) {
    return [...lines.slice(0, earlier.start), block, ...lines.slice(earlier.end)].join('\n');
  }
  const words = body.replace(/\s+$/, '');
  return words.length > 0 ? `${words}\n\n${block}\n` : `${block}\n`;
}
